import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import type { DroneRecord } from '@tonist/shared';
import { PageShell } from '../components/PageShell';
import { DroneCard } from '../components/DroneCard';
import { usePreview } from '../lib/preview';
import { useDrones } from '../store/dronesStore';
import { useSettings } from '../store/settingsStore';
import { useAuth } from '../store/authStore';
import { toast } from '../store/uiStore';
import { errorMessage } from '../lib/api';

export function DronesGallery() {
  const { playingId, toggle: togglePreview } = usePreview();
  const presets = useDrones((s) => s.presets);
  const mine = useDrones((s) => s.mine);
  const loading = useDrones((s) => s.loading);
  const { duplicate, remove, publish } = useDrones.getState();
  const setDroneIds = useSettings((s) => s.setDroneIds);
  const user = useAuth((s) => s.user);
  const navigate = useNavigate();
  const [busy, setBusy] = useState<string | null>(null);

  const play = (id: string) => { setDroneIds([id]); navigate('/'); };
  const run = async (id: string, fn: () => Promise<unknown>, ok?: string) => {
    setBusy(id);
    try { await fn(); if (ok) toast(ok); } catch (e) { toast(errorMessage(e), 'error'); } finally { setBusy(null); }
  };
  const share = (r: DroneRecord) => {
    const url = `${location.origin}/d/${r.slug}`;
    navigator.clipboard?.writeText(url).then(() => toast('Link copied'), () => toast(url));
  };

  return (
    <PageShell eyebrow="Drones" title="Presets and my drones" actions={<Link to="/drones/new" className="btn btn--primary">New drone</Link>}>
      <section className="stack">
        <h2>Mine {loading ? <span className="hint">(syncing…)</span> : null}</h2>
        {mine.length === 0 && <div className="notice">No drones of your own yet. Duplicate a preset below, or <Link to="/drones/new">start from scratch</Link>.{!user && <> Sign in to keep them across devices.</>}</div>}
        <div className="card-grid">
          {mine.map((r) => (
            <DroneCard key={r.id} drone={r.drone} preview={{ playing: playingId === r.drone.id, onToggle: () => togglePreview(r.drone) }}
              meta={<>{r.isPublic && r.slug ? <span>public · {r.plays} plays</span> : <span>private</span>}<span>{new Date(r.updatedAt).toLocaleDateString()}</span></>}
              actions={<>
                <button type="button" className="btn btn--sm btn--primary" onClick={() => play(r.id)}>Play</button>
                <Link to={`/drones/${r.id}`} className="btn btn--sm">Edit</Link>
                <button type="button" className="btn btn--sm" disabled={busy === r.id} onClick={() => void run(r.id, () => duplicate(r.id), 'Duplicated')}>Duplicate</button>
                {user && <button type="button" className="btn btn--sm" disabled={busy === r.id} onClick={() => void run(r.id, () => publish(r.id, !r.isPublic), r.isPublic ? 'Unpublished' : 'Published')}>{r.isPublic ? 'Unpublish' : 'Publish'}</button>}
                {r.isPublic && r.slug && <button type="button" className="btn btn--sm" onClick={() => share(r)}>Share</button>}
                <button type="button" className="btn btn--sm btn--danger" disabled={busy === r.id} onClick={() => { if (confirm(`Delete “${r.drone.name}”?`)) void run(r.id, () => remove(r.id), 'Deleted'); }}>Delete</button>
              </>} />
          ))}
        </div>
      </section>
      <section className="stack">
        <h2>Presets</h2>
        <div className="card-grid">
          {presets.map((d) => (
            <DroneCard key={d.id} drone={d} preview={{ playing: playingId === d.id, onToggle: () => togglePreview(d) }}
              actions={<>
                <button type="button" className="btn btn--sm btn--primary" onClick={() => play(d.id)}>Play</button>
                <Link to={`/drones/${d.id}`} className="btn btn--sm">Open</Link>
                <button type="button" className="btn btn--sm" disabled={busy === d.id} onClick={() => void run(d.id, async () => { const rec = await duplicate(d.id); navigate(`/drones/${rec.id}`); })}>Duplicate</button>
              </>} />
          ))}
        </div>
      </section>
    </PageShell>
  );
}
